import { ATRIBUTOS, type Pontos } from './escala'
import type { Raridade, Ruleset } from './ruleset'

/**
 * Os níveis comprados de um taco NFT, por atributo.
 *
 * Nível, e não pontos: o nível é o que foi PAGO, e os pontos são o que o
 * ruleset de hoje diz que ele vale. Rebalancear `pontosPorNivel` não toca em
 * nenhuma linha do banco. Raridade e arquétipo nunca sobem — não há nível
 * que mude o que o taco é, só o quanto ele rende.
 */
export type Atributo = keyof Pontos
export type Niveis = Record<Atributo, number>
export const NIVEIS_ZERO: Niveis = { peso: 0, efeito: 0, aderencia: 0, quebra: 0 }

export const niveisGastos = (n: Niveis): number => ATRIBUTOS.reduce((s, k) => s + n[k], 0)
export const orcamentoDeUpgrade = (raridade: Raridade, ruleset: Ruleset): number => ruleset.orcamentoDeNiveis[raridade]

/** Soma os níveis aos pontos base, preso no teto por atributo. */
export function pontosComNiveis(base: Pontos, niveis: Niveis, ruleset: Ruleset): Pontos {
  const fora = { ...base }
  for (const k of ATRIBUTOS) fora[k] = Math.min(ruleset.tetoPorAtributo, base[k] + niveis[k] * ruleset.pontosPorNivel)
  return fora
}

/**
 * Pode comprar mais um nível deste atributo? A ordem das recusas é a que o
 * jogador entende primeiro: acabou o orçamento do taco; este atributo já
 * está no máximo de níveis; este atributo já está no teto de pontos (pagaria
 * por nada).
 *
 * O TETO É POR NÍVEL INTEIRO: um atributo em 99 não sobe, porque o nível vale
 * 2 pontos e só 1 caberia. Ninguém paga o preço cheio por meio nível, e a
 * alternativa (vender o meio) faria o taco em 99 e o taco em 98 acabarem no
 * mesmo lugar por preços diferentes.
 *
 * Recebe os pontos BASE prontos (`pontosDoTaco(perfil, ruleset)`) e a
 * raridade em vez do perfil inteiro: `pontos.ts` precisa de `pontosComNiveis`
 * daqui, e chamar `pontosDoTaco` daqui fecharia um ciclo de import entre os
 * dois módulos. Quem chama já tem os pontos base em mãos de qualquer jeito.
 */
export function podeSubir(
  pontosBase: Pontos,
  raridade: Raridade,
  niveis: Niveis,
  atributo: Atributo,
  ruleset: Ruleset,
): 'ok' | 'orcamento' | 'nivelMaximo' | 'tetoDoAtributo' {
  if (niveisGastos(niveis) >= orcamentoDeUpgrade(raridade, ruleset)) return 'orcamento'
  if (niveis[atributo] >= ruleset.nivelMaximo) return 'nivelMaximo'
  const atual = pontosComNiveis(pontosBase, niveis, ruleset)
  if (atual[atributo] + ruleset.pontosPorNivel > ruleset.tetoPorAtributo) return 'tetoDoAtributo'
  return 'ok'
}
